import ConfirmModal from "components/shared-components/ConfirmModal";
import LoadingTableNormal from "components/shared-components/LoadingTableNormal";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import {
  useApproveStatement,
  useRejectStatement,
  useSaveStatement,
  useSignStatement,
  useStatementDetail,
  useSubmitStatement,
} from "hooks/invoiceStatement/useInvoiceStatement";
import { useStatementRole } from "hooks/invoiceStatement/useStatementRole";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useToasts } from "react-toast-notifications";
import { Button, Card, CardBody, Spinner } from "reactstrap";
import {
  APPROVE_STATUS_DRAFT,
  IS_TEST,
  APPROVE_STATUS_REJECTED,
  APPROVE_STATUS_SUBMITTED,
  CQT_STATUS_DRAFT,
  STATEMENT_PDF_STATUS_GENERATED,
  STATEMENT_TYPE_LABEL,
} from "../constants";
import { formatMonthLabel } from "../helper";
import {
  buildSavePayload,
  hasBlockingError,
  isFailedResponse,
  readResponseMessage,
  normalizeRowsForEdit,
  recalcAfter,
} from "../statementHelper";
import StatementParties from "./StatementParties";
import SubmitApprovalModal from "./SubmitApprovalModal";
import StatementPdfModal from "./StatementPdfModal";
import StatementRowsTable from "./StatementRowsTable";

const StepViewStatement = props => {
  const { statementId, onBack, embedded, onSigned } = props;

  const [detail, setDetail] = useState(null);
  const [rows, setRows] = useState([]);
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  // Chỉ cho xem PDF khi backend đã tạo xong file cho dữ liệu hiện tại:
  // hoặc detail báo pdf_status = generated, hoặc vừa lưu thành công.
  const [canViewPdf, setCanViewPdf] = useState(false);
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  // Trạng thái duyệt hiện tại; đổi ngay sau khi gửi duyệt / duyệt xong để
  // giao diện phản ánh đúng mà không phải tải lại chi tiết
  const [approveStatus, setApproveStatus] = useState(null);
  const { isRoleStore, isRoleAccountant } = useStatementRole();
  // Đã gửi yêu cầu ký thì khóa nút, tránh bấm hai lần tạo hai yêu cầu ký
  const [isSignSubmitted, setIsSignSubmitted] = useState(false);
  const { addToast } = useToasts();

  const mutation = useStatementDetail({
    onSuccess: payload => {
      const data = payload?.data;
      setDetail(data);
      setRows(normalizeRowsForEdit(data?.rows));
      setReason(data?.reason || "");
      setReasonError(false);
      setCanViewPdf(data?.pdf_status === STATEMENT_PDF_STATUS_GENERATED);
      setIsSignSubmitted(false);
      setApproveStatus(data?.approve_status || null);
    },
  });

  const signMutation = useSignStatement({
    onSuccess: payload => {
      const data = payload?.data;
      // HTTP 200 kèm result=false vẫn chạy vào đây nên phải tự kiểm
      if (data?.result === false) {
        addToast(data?.message || "Gửi yêu cầu ký số thất bại", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }
      setIsSignSubmitted(true);
      addToast(
        "Đã gửi yêu cầu ký. Chữ ký sẽ có sau khi hệ thống ký xử lý xong (vài giây).",
        { appearance: "success", autoDismiss: true }
      );
    },
  });

  const saveMutation = useSaveStatement({
    onSuccess: payload => {
      if (!isFailedResponse(payload?.data)) setCanViewPdf(true);
    },
  });

  // Mọi chỗ gọi save đều đi qua đây: result=false thì báo lỗi và dừng luồng
  const handleSaveResponse = (response, successText) => {
    if (isFailedResponse(response)) {
      addToast(readResponseMessage(response, "Lưu bảng kê thất bại"), {
        appearance: "error",
        autoDismiss: true,
      });
      return false;
    }
    if (successText) {
      addToast(successText, { appearance: "success", autoDismiss: true });
    }
    return true;
  };

  // Dùng chung cho Lưu và Lưu và ký số: lý do bắt buộc, các dòng đang điều
  // chỉnh phải có tên hàng hóa
  const validateRows = () => {
    if (!reason.trim()) {
      setReasonError(true);
      addToast("Vui lòng nhập lý do lập bảng kê", {
        appearance: "error",
        autoDismiss: true,
      });
      return false;
    }
    if (hasBlockingError(rows)) {
      addToast("Còn dòng chưa nhập tên hàng hóa, dịch vụ", {
        appearance: "error",
        autoDismiss: true,
      });
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validateRows()) return;
    saveMutation.mutate(buildSavePayload(detail, rows, reason, statementId), {
      onSuccess: (/** @type {any} */ response) =>
        handleSaveResponse(response, "Lưu bảng kê thành công"),
    });
  };

  const submitMutation = useSubmitStatement({});
  const approveMutation = useApproveStatement({});
  const rejectMutation = useRejectStatement({});

  // Cửa hàng bấm "Lưu và gửi duyệt": mở modal chọn email người mua
  const handleOpenSubmit = () => {
    if (!validateRows()) return;
    setIsSubmitOpen(true);
  };

  // Xác nhận trên modal: lưu nội dung trước, lưu xong mới gửi duyệt để kế toán
  // luôn duyệt đúng bản đang thấy. Lưu hỏng thì dừng, không gửi duyệt.
  const handleConfirmSubmit = email => {
    // Lưu kèm luôn email đã chọn, lưu xong mới gửi duyệt
    saveMutation.mutate(
      buildSavePayload(detail, rows, reason, statementId, email),
      {
        // Callback theo từng lần gọi nhận thẳng body API ({ result, message, data })
        onSuccess: (/** @type {any} */ saveResponse) => {
          if (!handleSaveResponse(saveResponse)) return;
          submitMutation.mutate(
            { statementId },
            {
              onSuccess: (/** @type {any} */ response) => {
                if (isFailedResponse(response)) {
                  addToast(
                    readResponseMessage(response, "Gửi duyệt thất bại"),
                    {
                      appearance: "error",
                      autoDismiss: true,
                    }
                  );
                  return;
                }
                setApproveStatus(APPROVE_STATUS_SUBMITTED);
                setIsSubmitOpen(false);
                addToast("Đã gửi bảng kê cho kế toán duyệt", {
                  appearance: "success",
                  autoDismiss: true,
                });
                if (onSigned) onSigned();
                else onBack();
              },
            }
          );
        },
      }
    );
  };

  // Kế toán từ chối duyệt: bảng kê quay về cho cửa hàng sửa, không ký.
  // note là lý do từ chối, ConfirmModal bắt buộc nhập trước khi gọi.
  const handleReject = note => {
    setIsRejectOpen(false);
    rejectMutation.mutate(
      { statementId, note },
      {
        onSuccess: (/** @type {any} */ response) => {
          if (isFailedResponse(response)) {
            addToast(readResponseMessage(response, "Từ chối duyệt thất bại"), {
              appearance: "error",
              autoDismiss: true,
            });
            return;
          }
          setApproveStatus(APPROVE_STATUS_REJECTED);
          addToast("Đã từ chối duyệt bảng kê", {
            appearance: "success",
            autoDismiss: true,
          });
          if (onSigned) onSigned();
          else onBack();
        },
      }
    );
  };

  // Kế toán duyệt: duyệt xong mới gửi yêu cầu ký, ký xong thì rời màn hình
  const handleApprove = () => {
    setIsApproveOpen(false);
    approveMutation.mutate(statementId, {
      onSuccess: (/** @type {any} */ response) => {
        if (isFailedResponse(response)) {
          addToast(readResponseMessage(response, "Duyệt bảng kê thất bại"), {
            appearance: "error",
            autoDismiss: true,
          });
          return;
        }
        addToast("Đã duyệt bảng kê, đang gửi yêu cầu ký", {
          appearance: "success",
          autoDismiss: true,
        });
        signMutation.mutate(statementId, {
          onSuccess: (/** @type {any} */ signResponse) => {
            if (signResponse?.result === false) return;
            if (onSigned) onSigned();
            else onBack();
          },
        });
      },
    });
  };

  const isSignBusy =
    saveMutation.isLoading ||
    signMutation.isLoading ||
    submitMutation.isLoading ||
    approveMutation.isLoading ||
    rejectMutation.isLoading;

  // Chỉ bảng kê nháp mới được sửa. Đã gửi yêu cầu ký thì coi như hết nháp
  // luôn, dù detail chưa tải lại trạng thái mới.
  const isDraft = detail?.cqt_status === CQT_STATUS_DRAFT && !isSignSubmitted;
  // Chưa gửi duyệt thì approve_status rỗng hoặc M
  const isWaitingSubmit =
    !approveStatus || approveStatus === APPROVE_STATUS_DRAFT;
  // Chỉ sửa được khi còn nháp và chưa gửi duyệt. Đã gửi duyệt, đã duyệt hoặc
  // bị từ chối duyệt thì chỉ xem.
  const canEdit = IS_TEST || (isDraft && isWaitingSubmit);
  // IS_TEST: hiện hết nút để thử nghiệm, không xét quyền và trạng thái duyệt
  // Màn lập bảng kê (không nhúng trong modal): người vừa lập được gửi duyệt
  // luôn, không xét quyền. Mở từ danh sách thì vẫn phải là cửa hàng.
  const canSubmit =
    IS_TEST || (isDraft && isWaitingSubmit && (!embedded || isRoleStore));
  const canApprove =
    IS_TEST ||
    (isDraft && approveStatus === APPROVE_STATUS_SUBMITTED && isRoleAccountant);

  const { mutate } = mutation;

  useEffect(() => {
    if (statementId) {
      mutate(statementId);
    }
  }, [statementId, mutate]);

  // Sửa ô nào cũng tính lại 3 cột tiền của dòng đó, chênh lệch suy ra sau
  const handleChangeRow = (index, field, value) => {
    // Nội dung đã đổi thì file PDF cũ không còn khớp, phải lưu lại
    setCanViewPdf(false);
    setRows(prev =>
      prev.map((row, position) => {
        if (position !== index) return row;
        return {
          ...row,
          after: recalcAfter({ ...(row.after || {}), [field]: value }),
        };
      })
    );
  };

  return (
    <React.Fragment>
      <StatementParties seller={detail?.seller} buyer={detail?.buyer} />

      <Card>
        <CardBody>
          <div className="mb-3">
            {/* Số bảng kê là thứ phân biệt các bảng kê với nhau nên để nổi
                nhất, đứng trước cả tiêu đề */}
            {detail?.code && (
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="text-muted font-size-11 text-uppercase">
                  Số bảng kê
                </span>
                <span className="badge badge-soft-primary font-size-15 fw-bold px-3 py-2">
                  {detail.code}
                </span>
              </div>
            )}
            <h5 className="font-size-15 mb-1">
              Bảng kê các hóa đơn điện tử đã lập sai
            </h5>
            <div className="text-muted font-size-12">
              Hình thức:{" "}
              <span className="text-body fw-bold">
                {STATEMENT_TYPE_LABEL[detail?.adjust_mode] || ""}
              </span>
              {detail?.period && (
                <React.Fragment>
                  {" · "}Kỳ Hóa đơn:{" "}
                  <span className="text-body fw-bold">
                    {formatMonthLabel(detail.period)}
                  </span>
                </React.Fragment>
              )}
              {/* {" · "}cột (1)–(28) */}
            </div>
          </div>

          {mutation.isLoading ? (
            <LoadingTableNormal countColumn="10" countRow="5" />
          ) : rows.length > 0 ? (
            <StatementRowsTable
              rows={rows}
              invoice={detail?.invoice}
              onChangeRow={handleChangeRow}
              readOnly={!canEdit}
            />
          ) : (
            <div className="text-center text-muted py-4">
              Chưa có dòng hóa đơn nào trong bảng kê
            </div>
          )}

          <div className="mt-3">
            <div className="text-muted font-size-12 mb-1 fw-bold">
              Lý do <span className="text-danger">*</span>
            </div>
            <textarea
              className={`form-control ${reasonError ? "is-invalid" : ""}`}
              rows="2"
              placeholder="Nhập lý do điều chỉnh"
              readOnly={!canEdit}
              value={reason}
              onChange={event => {
                setReason(event.target.value);
                if (event.target.value.trim()) setReasonError(false);
              }}
            />
            {reasonError && (
              <MessageFormValidate error="Vui lòng nhập lý do lập bảng kê" />
            )}
          </div>
        </CardBody>
      </Card>

      <ConfirmModal
        isOpen={isApproveOpen}
        toggle={() => setIsApproveOpen(prev => !prev)}
        titleHeader="Xác nhận duyệt bảng kê"
        titleBody={`Duyệt bảng kê ${
          detail?.code || ""
        }? Sau khi duyệt, hệ thống sẽ ký bên bán và gửi link ký cho người mua.`}
        textBtnCancel="Hủy"
        textBtnConfirm="Duyệt"
        isDisableButton={approveMutation.isLoading || signMutation.isLoading}
        onConfirm={handleApprove}
      />

      <ConfirmModal
        isOpen={isRejectOpen}
        toggle={() => setIsRejectOpen(prev => !prev)}
        titleHeader="Từ chối duyệt bảng kê"
        titleBody="Lý do từ chối"
        isHaveNote
        isRequireNote
        notePlateHolder="Nhập lý do từ chối duyệt"
        textBtnCancel="Hủy"
        textBtnConfirm="Từ chối duyệt"
        isDisableButton={rejectMutation.isLoading}
        onConfirm={handleReject}
      />

      <SubmitApprovalModal
        isOpen={isSubmitOpen}
        toggle={() => setIsSubmitOpen(prev => !prev)}
        buyer={detail?.buyer}
        code={detail?.code}
        isSubmitting={saveMutation.isLoading || submitMutation.isLoading}
        onConfirm={handleConfirmSubmit}
      />

      <StatementPdfModal
        isOpen={isPdfOpen}
        toggle={() => setIsPdfOpen(prev => !prev)}
        statementId={statementId}
        code={detail?.code}
      />

      {/* Chừa chỗ để thanh cố định không che mất dòng cuối của bảng.
          Trong modal thì thanh nằm theo dòng chảy nên không cần chừa. */}
      {!embedded && <div style={{ height: 84 }} />}
      {/*
        Dùng position fixed chứ không phải sticky: layout ngang bọc nội dung
        trong .main-content vốn có overflow: hidden, sticky sẽ không dính.
      */}
      <div
        className="d-flex justify-content-between align-items-center gap-3 border-top py-3 px-4"
        style={
          embedded
            ? undefined
            : {
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 100,
                background: "var(--bs-body-bg)",
                boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.06)",
              }
        }
      >
        <Button type="button" color="secondary" outline onClick={onBack}>
          <i className={`bx me-1 ${embedded ? "bx-x" : "bx-arrow-back"}`} />
          {embedded ? "Đóng" : "Quay lại"}
        </Button>
        <div className="d-flex gap-2">
          {canEdit && (
            <Button
              type="button"
              color="success"
              disabled={!statementId || isSignBusy}
              onClick={handleSave}
            >
              {saveMutation.isLoading && !isSubmitOpen ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="bx bx-save me-1" />
              )}
              Lưu
            </Button>
          )}
          {/* Cửa hàng gửi bảng kê cho kế toán duyệt */}
          {canSubmit && (
            <Button
              type="button"
              color="primary"
              disabled={!statementId || isSignBusy}
              onClick={handleOpenSubmit}
            >
              <i className="bx bx-send me-1" />
              Lưu và gửi duyệt
            </Button>
          )}
          {/* Kế toán duyệt; duyệt xong hệ thống ký bên bán rồi gửi link cho
              người mua */}
          {canApprove && (
            <Button
              type="button"
              color="primary"
              disabled={!statementId || isSignBusy}
              onClick={() => setIsApproveOpen(true)}
            >
              {approveMutation.isLoading || signMutation.isLoading ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="bx bxs-check-circle me-1" />
              )}
              Duyệt
            </Button>
          )}
          {canApprove && (
            <Button
              type="button"
              color="danger"
              outline
              disabled={!statementId || isSignBusy}
              onClick={() => setIsRejectOpen(true)}
            >
              {rejectMutation.isLoading ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="bx bxs-x-circle me-1" />
              )}
              Từ chối duyệt
            </Button>
          )}
          {canViewPdf && (
            <Button
              type="button"
              color="danger"
              disabled={!statementId}
              onClick={() => setIsPdfOpen(true)}
            >
              <i className="bx bxs-file-pdf me-1" />
              Xem PDF
            </Button>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

StepViewStatement.propTypes = {
  statementId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onBack: PropTypes.func.isRequired,
  embedded: PropTypes.bool,
  // Gọi sau khi gửi ký thành công, trước khi đóng (vd. tải lại danh sách)
  onSigned: PropTypes.func,
};

StepViewStatement.defaultProps = {
  statementId: null,
  embedded: false,
  onSigned: undefined,
};

export default StepViewStatement;
